import { useMemo, useState } from 'react';
import { useLiveRxQuery } from 'rxdb/plugins/react';
import { useStableQuery } from '../../db/useStableQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { PickerDay, type PickerDayProps } from '@mui/x-date-pickers/PickerDay';
import dayjs, { type Dayjs } from 'dayjs';
import type { TimeEntryDocType } from '@ast-data/shared';
import { formatDate } from '../../utils/formatDate';
import { shiftDay, todayIso, isoWeekYear } from '../timeTracking/timeMath';
import {
    type TimeEntryVariant,
    WORKER_TIME_ENTRY_VARIANT,
    EQUIPMENT_TIME_ENTRY_VARIANT
} from '../timeTracking/timeEntryVariant';
import { useActiveFieldProject } from './useActiveFieldProject';
import FieldTimeEntryGrid from './FieldTimeEntryGrid';
import { useShownJson } from '../users/personLabels';

/** A stored 'YYYY-MM-DD' as a picker bound, or undefined when unset/unparsable. */
function dayBound(value: string | null): Dayjs | undefined {
    if (!value) {
        return undefined;
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : undefined;
}

/**
 * How far apart the day stepper's ◀ ▶ may get (px). Roughly a phone's content
 * width, so ◀ ▶ keep hugging the date on a wide page instead of drifting to the
 * far edges — and a phone, being narrower than this anyway, is unaffected.
 */
const STEPPER_MAX_WIDTH = 400;

/**
 * A 'YYYY-MM-DD' day held within [firstDay, lastDay] — an unset firstDay leaves
 * the lower end open. ISO days compare correctly as plain strings.
 */
function clampDay(
    value: string,
    firstDay: string | null,
    lastDay: string
): string {
    if (value > lastDay) {
        return lastDay;
    }
    if (firstDay && value < firstDay) {
        return firstDay;
    }
    return value;
}

/**
 * The day the ◀ / ▶ stepper lands on from `fromDay`, stepping `delta` (±1) days
 * and skipping Sundays (no work on Sundays), then clamped to [firstDay, lastDay].
 * Returns null when no valid day lies that way — i.e. the arrow should be off.
 */
function resolveStep(
    fromDay: string,
    delta: number,
    firstDay: string | null,
    lastDay: string
): string | null {
    let next = shiftDay(fromDay, delta);
    // delta is ±1, so one extra step in the same direction clears a Sunday.
    if (next && dayjs(next).day() === 0) {
        next = shiftDay(next, Math.sign(delta));
    }
    if (!next || next !== clampDay(next, firstDay, lastDay)) {
        return null;
    }
    return next;
}

/** German short weekday for a 'YYYY-MM-DD' day (labels stay German, like dates). */
function weekdayShort(dateStr: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return '';
    }
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('de-DE', {
        weekday: 'short'
    });
}

interface FieldTimeEntriesPageProps {
    /** Which Zeiterfassung this day view records — Mitarbeiter or Geräte. */
    variant: TimeEntryVariant;
}

/**
 * Touch-first single-day Zeiterfassung for the field: one day at a time (step
 * with ◀/▶), backed by an inline-editable grid of every worker's — or Gerät's —
 * hours for the shown day (see FieldTimeEntryGrid). The day stepper, its dotted
 * calendar and the Maßnahme bounds are shared by both variants; only the rows
 * below differ.
 */
export function FieldTimeEntriesPage({ variant }: FieldTimeEntriesPageProps) {
    const intl = useIntl();
    const { projectId = '' } = useParams<{ projectId: string }>();
    const [searchParams] = useSearchParams();
    // `?date=…` opens a particular day — how the Tagebuch's Arbeitszeit hint
    // points at the entries it derived its times from. Only the STARTING day;
    // the arrows and the picker take over from there.
    const [day, setDay] = useState(
        () => searchParams.get('date') || todayIso()
    );
    const [dayPickerOpen, setDayPickerOpen] = useState(false);
    // The picker's own selection, so OK commits it and Cancel drops it. Seeded
    // from `day` each time it opens.
    const [pickedDay, setPickedDay] = useState<Dayjs | null>(null);

    function openDayPicker() {
        setPickedDay(dayjs(shownDay));
        setDayPickerOpen(true);
    }

    const project = useActiveFieldProject(projectId);

    // Every day of this Maßnahme that already has hours on it, so the calendar
    // can mark them. One subscription for the whole project — a query per
    // rendered day would be 42 of them per month shown.
    const entriesQuery = useStableQuery<TimeEntryDocType>({
        selector: { projectId, entryType: variant.type }
    });
    const { results: entriesDocs } = useLiveRxQuery<TimeEntryDocType>({
        collection: 'timeEntries',
        query: entriesQuery
    });
    const entries = useShownJson<TimeEntryDocType>('timeEntry', entriesDocs);
    const daysWithEntries = useMemo(() => {
        const days = new Set<string>();
        for (const entry of entries) {
            if (entry.date) {
                days.add(entry.date);
            }
        }
        return days;
    }, [entries]);

    // A day cell with a dot when hours are logged on it. Built here, closing over
    // the day set, rather than at module scope with the set handed down through
    // `slotProps.day` — the slot's prop type admits no extra fields, so that route
    // needs an `any` cast. It is rebuilt only when the set itself changes.
    const pickerSlots = useMemo(
        () => ({
            day: function DayWithEntries({
                day: dayValue,
                outsideCurrentMonth,
                ...dayProps
            }: PickerDayProps) {
                const hasEntries =
                    !outsideCurrentMonth &&
                    daysWithEntries.has(dayValue.format('YYYY-MM-DD'));
                return (
                    <Badge
                        overlap="circular"
                        variant="dot"
                        color="primary"
                        invisible={!hasEntries}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right'
                        }}
                    >
                        <PickerDay
                            {...dayProps}
                            day={dayValue}
                            outsideCurrentMonth={outsideCurrentMonth}
                        />
                    </Badge>
                );
            }
        }),
        [daysWithEntries]
    );

    // The Maßnahme's span bounds every way of choosing a day (◀ ▶, the picker,
    // "Heute"). 'YYYY-MM-DD' strings compare correctly as text, so no dates are
    // parsed here. Either end may be unrecorded: a blank startDate leaves the
    // lower end open, and the upper end falls back to today.
    const firstDay = project?.startDate || null;
    const projectEnd = project?.endDate || null;
    // The last day hours can be logged for: today, unless the Maßnahme ended
    // earlier — you can't log work before it happens either way.
    const lastDay =
        projectEnd && projectEnd < todayIso() ? projectEnd : todayIso();
    // A Maßnahme still to come has no loggable day at all.
    const notStarted = firstDay !== null && firstDay > todayIso();
    const ended = lastDay !== todayIso();

    /**
     * `day` starts at today, which is out of range on a Maßnahme that has already
     * ended — and the project loads async, so the bounds only arrive later.
     * Clamping on read (rather than correcting the state in an effect) keeps the
     * shown day valid without a second render pass. A Sunday (e.g. today, or the
     * span's last day) snaps back to Saturday — no work on Sundays; the second
     * clamp keeps that in range should the span itself begin on a Sunday.
     */
    const clampedDay = clampDay(day, firstDay, lastDay);
    const shownDay =
        dayjs(clampedDay).day() === 0
            ? clampDay(shiftDay(clampedDay, -1), firstDay, lastDay)
            : clampedDay;

    // The most recent workday — today, or Saturday when today is a Sunday. What
    // the "heute" button jumps to (and hides on, when already there).
    const todayStr = todayIso();
    const currentWorkday =
        dayjs(todayStr).day() === 0 ? shiftDay(todayStr, -1) : todayStr;

    // The days the ◀ / ▶ arrows land on (Sunday-skipped, clamped) — null = no day
    // that way, so the arrow is disabled and can't leave an inert-but-enabled edge.
    const prevDay = resolveStep(shownDay, -1, firstDay, lastDay);
    const nextDay = resolveStep(shownDay, 1, firstDay, lastDay);

    function stepDay(target: string | null) {
        if (target) {
            setDay(target);
        }
    }

    if (notStarted) {
        return (
            <Alert severity="info">
                <FormattedMessage
                    id="field.timeEntries.notStarted"
                    values={{ date: formatDate(firstDay) }}
                />
            </Alert>
        );
    }

    return (
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            {ended && (
                <Alert severity="info">
                    <FormattedMessage
                        id="field.timeEntries.ended"
                        values={{ date: formatDate(lastDay) }}
                    />
                </Alert>
            )}
            {/* Day stepper */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    /**
                     * Capped and centred, so a wider page (a tablet, where the
                     * grid wants the room) doesn't fling ◀ ▶ out to the edges,
                     * away from the date they step. Centred with `alignSelf`, NOT
                     * `mx: auto`: Stack's spacing (useFlexGap defaults off) resets
                     * `margin: 0` on every child through a selector that
                     * outspecifies this one, so an auto margin here is dropped.
                     */
                    width: '100%',
                    maxWidth: STEPPER_MAX_WIDTH,
                    alignSelf: 'center'
                }}
            >
                <IconButton
                    size="large"
                    aria-label={intl.formatMessage({
                        id: 'field.timeEntries.prevDay'
                    })}
                    // Not before the Maßnahme began (nor onto a Sunday off the edge).
                    disabled={prevDay === null}
                    onClick={() => stepDay(prevDay)}
                >
                    <ChevronLeftIcon fontSize="large" />
                </IconButton>
                <Box sx={{ textAlign: 'center' }}>
                    <ButtonBase
                        aria-label={intl.formatMessage({
                            id: 'field.timeEntries.pickDay'
                        })}
                        onClick={openDayPicker}
                        sx={{
                            borderRadius: 1,
                            px: 1,
                            py: 0.25,
                            alignItems: 'center'
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                lineHeight: 1.2
                            }}
                        >
                            {weekdayShort(shownDay)} {formatDate(shownDay)}
                        </Typography>
                        {/*
                         * Centring aligns the two BOXES, and the date is a title
                         * (TITLE_FONT) while this is body sans, so their boxes
                         * centre without their text lining up. Baselining drops it
                         * too far — hence a one-off optical nudge, tuned by eye.
                         */}
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.2,
                                ml: 1,
                                position: 'relative',
                                top: '2px'
                            }}
                        >
                            <FormattedMessage id="timeEntry.field.week" />{' '}
                            {isoWeekYear(shownDay).week}
                        </Typography>
                    </ButtonBase>
                    {/* Hidden once the Maßnahme is over — today is out of range. */}
                    {shownDay !== currentWorkday && !ended && (
                        <Button
                            size="small"
                            startIcon={<TodayIcon />}
                            onClick={() => setDay(currentWorkday)}
                        >
                            <FormattedMessage id="field.timeEntries.today" />
                        </Button>
                    )}
                </Box>
                <IconButton
                    size="large"
                    aria-label={intl.formatMessage({
                        id: 'field.timeEntries.nextDay'
                    })}
                    // Not past today / the Maßnahme's end (nor onto a Sunday off the edge).
                    disabled={nextDay === null}
                    onClick={() => stepDay(nextDay)}
                >
                    <ChevronRightIcon fontSize="large" />
                </IconButton>
            </Box>
            {/*
             * Jump to any day of the Maßnahme. MobileDatePicker (not DatePicker)
             * for the touch-first calendar dialog, and because its dialog is
             * centred rather than anchored to a field — this one has no visible
             * field, the date label above opens it.
             */}
            <MobileDatePicker
                open={dayPickerOpen}
                onClose={() => setDayPickerOpen(false)}
                // Controlled by `pickedDay`, NOT by `day`: a picker given a value
                // without an onChange can never update, so every pick would be
                // discarded and OK would hand back the day it opened on.
                value={pickedDay}
                onChange={setPickedDay}
                onAccept={(picked) => {
                    if (picked?.isValid()) {
                        setDay(picked.format('YYYY-MM-DD'));
                    }
                }}
                // The same bounds as the ◀ ▶ buttons (lastDay already caps the
                // upper end at today, so no disableFuture needed on top).
                minDate={dayBound(firstDay)}
                maxDate={dayBound(lastDay)}
                // No time entries on Sundays.
                shouldDisableDate={(dateValue) => dateValue.day() === 0}
                slots={pickerSlots}
                slotProps={{ textField: { sx: { display: 'none' } } }}
            />
            <FieldTimeEntryGrid
                variant={variant}
                projectId={projectId}
                day={shownDay}
            />
        </Stack>
    );
}

/** Route element: the field Zeiterfassung - Mitarbeiter for one day. */
export default function FieldWorkerTimeEntriesPage() {
    return <FieldTimeEntriesPage variant={WORKER_TIME_ENTRY_VARIANT} />;
}

/** Route element: the field Zeiterfassung - Geräte for one day. */
export function FieldEquipmentTimeEntriesPage() {
    return <FieldTimeEntriesPage variant={EQUIPMENT_TIME_ENTRY_VARIANT} />;
}
